import { canRestoreInto, parseBackup, type RepwiseEvent } from '@repwise/domain';
import { useState } from 'react';
import { useEvents, useRepo } from '../lib/hooks.ts';
import { Button, Card } from './ui.tsx';

/** Blob.text() where available; FileReader otherwise (older engines, test DOMs). */
export const readText = (f: File): Promise<string> =>
  typeof f.text === 'function'
    ? f.text()
    : new Promise((ok, fail) => {
        const r = new FileReader();
        r.onload = () => ok(String(r.result));
        r.onerror = () => fail(r.error);
        r.readAsText(f);
      });

/** US-28: restore a Repwise backup (our JSON, or the automatic backup). Only into an account with no workouts yet. */
export function RestoreBackup({ onRestored }: { onRestored?: () => void }) {
  const { store } = useRepo();
  const events = useEvents();
  const [parsed, setParsed] = useState<{ events: RepwiseEvent[]; skipped: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  if (!events) return null;
  if (!canRestoreInto(events))
    return <p className="text-sm text-text-muted">Restoring works on an account with no workouts yet, so it can't mix two histories. Your workouts are already backed up to your account.</p>;
  if (done) return <p role="status">Backup restored.</p>;
  const workouts = parsed?.events.filter((e) => e.type === 'workout_finished' || e.type === 'workout_imported').length ?? 0;
  return (
    <div className="space-y-3">
      <label className="block">
        <span className="text-sm">Repwise backup file (.json)</span>
        <input
          type="file"
          accept=".json,application/json"
          className="block mt-1 min-h-12"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (!f)
              return;
            void readText(f).then((t) => {
              try {
                setParsed(parseBackup(t));
                setError(null);
              } catch (err) {
                setParsed(null);
                setError(err instanceof Error ? err.message : "This file couldn't be read.");
              }
            });
          }}
        />
      </label>
      {error && (
        <p className="text-miss text-sm" role="alert">
          {error}
        </p>
      )}
      {parsed && (
        <Card>
          <p>
            {workouts} workout{workouts === 1 ? '' : 's'} and your settings ({parsed.events.length} changes).
          </p>
          {parsed.skipped > 0 && <p className="text-sm text-text-muted">{parsed.skipped} unreadable or repeated entries will be left out.</p>}
          <div className="mt-3">
            <Button
              onClick={() =>
                void store.append(parsed.events).then(() => {
                  setDone(true);
                  onRestored?.();
                })
              }
            >
              Restore this backup
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
